import type { TopicInterpretationViewModel } from './presentation.js';
import type { TopicSynthesisFacet, TopicSynthesisViewModel } from './topicSynthesis.js';

export interface FullChartThread {
  topic: string;
  topicLabel: string;
  headline: string;
  focus: string;
  subjectSummary: string;
  supportCount: number;
  counterCount: number;
  evidenceSummary: string;
  facets: readonly TopicSynthesisFacet[];
  basisLabels: readonly string[];
}

export interface FullChartInteraction {
  id: string;
  label: string;
  topics: readonly string[];
  reading: string;
  patternReading: string;
  balanceReading: string;
  fitReason: string;
  facetLabels: readonly string[];
  priority: number;
}

export interface FullChartStructuralConvergence {
  id: string;
  label: string;
  topics: readonly string[];
  reading: string;
}

export interface FullChartTension {
  id: string;
  topic: string;
  topicLabel: string;
  reading: string;
}

export interface FullChartSynthesisViewModel {
  model: 'FULL-CHART-SYNTHESIS-0.7.0';
  headline: string;
  lead: string;
  threads: readonly FullChartThread[];
  interactions: readonly FullChartInteraction[];
  structuralConvergences: readonly FullChartStructuralConvergence[];
  tensions: readonly FullChartTension[];
  balanceSummary: string;
  coverageSummary: string;
  disclosure: string;
}

const TOPIC_LABEL: Readonly<Record<string,string>> = Object.freeze({
  personality:'個性', career:'事業', wealth:'財運', relationship:'感情', social:'人際', family:'家庭',
  home:'居住與家庭空間', wellbeing:'休息與內在節奏',
});

const ALLOWED_INTERACTION_DEFS = Object.freeze([
  { id:'self-work', label:'做事方式 × 工作承接', a:'personality', b:'career', bridge:'把平常的決策與反應方式，和進入工作責任後的承接節奏放在一起看。' },
  { id:'self-resource', label:'做事方式 × 資源安排', a:'personality', b:'wealth', bridge:'把平常怎麼判斷，和時間、金錢、注意力怎麼配置放在一起看。' },
  { id:'self-social', label:'做事方式 × 外部合作', a:'personality', b:'social', bridge:'把個人的處理方式，和需要分工、求助、協調時的互動方式放在一起看。' },
  { id:'self-partner', label:'做事方式 × 親密互動', a:'personality', b:'relationship', bridge:'把自己習慣的處理方式，和一對一親密互動中的協調方式放在一起看。' },
  { id:'self-rest', label:'用力方式 × 恢復節奏', a:'personality', b:'wellbeing', bridge:'把平常怎麼投入與處理壓力，和怎麼真正切回休整放在一起看。' },
  { id:'work-resource', label:'工作承接 × 資源配置', a:'career', b:'wealth', bridge:'把工作責任怎麼承接，和時間、金錢、注意力怎麼分配放在一起看。' },
  { id:'work-network', label:'工作角色 × 合作網絡', a:'career', b:'social', bridge:'把工作角色本身，和實際能否調動合作與支援放在一起看。' },
  { id:'work-rest', label:'工作節奏 × 恢復方式', a:'career', b:'wellbeing', bridge:'把長期承接責任的方式，和忙完後能不能真正恢復放在一起看。' },
  { id:'partner-network', label:'親密互動 × 外部合作', a:'relationship', b:'social', bridge:'把一對一關係和群體合作分開比較，觀察你是否會切換不同的協調方式。' },
  { id:'family-partner', label:'近身家庭 × 伴侶互動', a:'family', b:'relationship', bridge:'把近身家庭的責任協調，和伴侶之間的分工與溝通放在一起看。' },
  { id:'family-home', label:'家庭協調 × 居住安排', a:'family', b:'home', bridge:'把家庭成員怎麼分工，和居住空間怎麼維持、調整放在一起看。' },
  { id:'home-rest', label:'生活空間 × 休整節奏', a:'home', b:'wellbeing', bridge:'把居住環境的維持方式，和休息、恢復的節奏放在一起看。' },
  { id:'social-resource', label:'合作網絡 × 資源邊界', a:'social', b:'wealth', bridge:'把人情合作與支援網絡，和自己的時間、金錢、注意力邊界放在一起看。' },
] as const);

function topicCounts(topic:TopicInterpretationViewModel):{support:number;counter:number} {
  return topic.claims.reduce((acc,claim)=>({
    support:acc.support+claim.supportingRuleCount,
    counter:acc.counter+claim.counterRuleCount,
  }),{support:0,counter:0});
}
function threadFor(topic:TopicInterpretationViewModel,synthesis:TopicSynthesisViewModel|undefined):FullChartThread|undefined {
  if(!synthesis || synthesis.status!=='available' || !synthesis.facets.length) return undefined;
  const counts=topicCounts(topic);
  const selected=synthesis.facets.slice(0,3);
  const focus=selected.map((facet)=>facet.label).join('、');
  const subjectSummary=selected.map((facet)=>facet.subjectLabel).filter((item)=>item&&item!=='命盤結構').join('、') || '已觸發規則';
  const basisLabels=Object.freeze([...new Set(selected.flatMap((facet)=>facet.basisLabels))]);
  const evidenceSummary=counts.support>0&&counts.counter>0
    ? `這一面向同時有可運用條件與需要調整的條件，主要集中在 ${focus}。`
    : counts.counter>0
      ? `這一面向目前較需要留意 ${focus} 在壓力情境下怎麼調整，不代表負面結果。`
      : `這一面向目前較一致地指向 ${focus}。`;
  return Object.freeze({
    topic:topic.topic, topicLabel:TOPIC_LABEL[topic.topic]??topic.topic, headline:synthesis.headline, focus, subjectSummary,
    supportCount:counts.support, counterCount:counts.counter, evidenceSummary,
    facets:Object.freeze(selected), basisLabels,
  });
}

function stateWeight(facet:TopicSynthesisFacet):number {
  if(facet.balanceState==='mixed') return 4;
  if(facet.balanceState==='counter_only') return 3;
  return 2;
}

const PRIORITY_BASIS=Object.freeze(['廟旺／亮度','四化','三方四正','煞曜／空劫'] as const);
const READER_BASIS_LABEL:Readonly<Record<string,string>>=Object.freeze({
  '廟旺／亮度':'發揮條件', '四化':'議題聚焦條件', '三方四正':'周邊互動條件', '煞曜／空劫':'阻力條件',
});

function sharedBasis(a:FullChartThread,b:FullChartThread):readonly string[] {
  return Object.freeze(PRIORITY_BASIS.filter((label)=>a.basisLabels.includes(label)&&b.basisLabels.includes(label)));
}
function sharedFacetBasis(a:TopicSynthesisFacet,b:TopicSynthesisFacet):readonly string[] {
  return Object.freeze(PRIORITY_BASIS.filter((label)=>a.basisLabels.includes(label)&&b.basisLabels.includes(label)));
}
function facetPairScore(a:TopicSynthesisFacet,b:TopicSynthesisFacet):number {
  const shared=Math.min(sharedFacetBasis(a,b).length,2)*3;
  const tension=(a.balanceState==='support_only'?0:1)+(b.balanceState==='support_only'?0:1);
  return stateWeight(a)+stateWeight(b)+shared+tension;
}
function bestFacetPair(a:FullChartThread,b:FullChartThread):readonly [TopicSynthesisFacet,TopicSynthesisFacet] {
  let best:readonly [TopicSynthesisFacet,TopicSynthesisFacet]=[a.facets[0]!,b.facets[0]!];
  let bestScore=facetPairScore(best[0],best[1]);
  for(const af of a.facets){
    for(const bf of b.facets){
      const score=facetPairScore(af,bf);
      if(score>bestScore){ best=[af,bf]; bestScore=score; }
    }
  }
  return best;
}
function pairPriority(a:FullChartThread,b:FullChartThread):number {
  const depth=Math.min(a.facets.length,3)+Math.min(b.facets.length,3);
  const state=Math.max(...a.facets.map(stateWeight))+Math.max(...b.facets.map(stateWeight));
  const tension=(a.counterCount>0?2:0)+(b.counterCount>0?2:0);
  const repeated=Math.min(sharedBasis(a,b).length,2)*2;
  return depth+state+tension+repeated;
}

function facetStateText(facet:TopicSynthesisFacet):string {
  if(facet.balanceState==='mixed') return '本身就同時有推力與牽制';
  if(facet.balanceState==='counter_only') return '目前更需要靠情境調整';
  return '目前較有一致的可運用方向';
}

const INTERACTION_ROLE_READING:Readonly<Record<string,string>>=Object.freeze({
  'self-work':'先看平常如何收斂問題與排優先，再看工作責任是否讓這種做法變成穩定輸出；如果工作邊界一直改，重點是調整角色而不是反覆證明自己能撐。',
  'self-resource':'先看平常怎麼做決定，再檢查這種決策速度是否會改變時間、金錢與注意力的配置；重點是讓判斷方式服務長期資源邊界，而不是每次都被眼前需求改寫。',
  'self-social':'先看自己遇事時習慣怎麼處理，再看合作場景是否需要換成分工、求助或協調；能獨立處理不等於每次都該自己補位。',
  'self-partner':'先辨認自己平常如何處理差異，再看一對一關係裡是否留下重新協商的空間；重點不是誰照誰的方式，而是兩種處理節奏能不能被說清楚。',
  'self-rest':'先看自己投入與收斂問題的方式，再檢查休息時是否真的把同一套運轉模式停下來；如果只是換一件事繼續處理，恢復節奏仍可能被工作式思考佔滿。',
  'work-resource':'先確認哪些工作責任真正值得長期承接，再決定要分配多少時間、注意力與其他資源；若角色本身持續變動，資源配置也應跟著重排，而不是靠額外投入維持舊安排。',
  'work-network':'先確認工作角色的責任邊界，再看哪些部分應由合作網絡承接；最值得觀察的是「可靠」是否變成所有事情最後都回到自己身上。',
  'work-rest':'先看工作責任是否有明確收尾點，再看休整方式能否真正讓注意力離開未完成事項；沒有收尾邊界時，休息時間很容易繼續被工作佔用。',
  'partner-network':'先分開看一對一協作與群體合作的規則，再比較自己是否在兩種關係裡用了不同的界線與求助方式；差異本身不代表好壞，重點是切換是否清楚。',
  'family-partner':'先分清楚原生／近身家庭的既有責任，再看伴侶間哪些事情需要重新協商；不要把熟悉的家庭分工直接當成親密關係裡理所當然的規則。',
  'family-home':'先釐清家人之間誰負責什麼，再看空間、維護與共同資源是否支持這套分工；如果同一個空間問題反覆出現，值得檢查的往往是使用規則而不只是物品本身。',
  'home-rest':'先看環境裡哪些安排會持續拉走注意力，再看休息方式是否真的能切換狀態；有時要調整的不是「多休息」，而是讓空間本身少製造未完成感。',
  'social-resource':'先確認哪些合作關係真的值得投入，再檢查人情、求助與補位是否正在吃掉原本保留的時間、金錢或注意力；可用的人際網絡應降低耗損，而不是讓資源邊界變得更模糊。',
});

function interactionReading(a:FullChartThread,b:FullChartThread,bridge:string,facets:readonly [TopicSynthesisFacet,TopicSynthesisFacet]):string {
  const [af,bf]=facets;
  return `${bridge} 這組合參在「${a.topicLabel}」要看的是「${af.label}」，在「${b.topicLabel}」要看的是「${bf.label}」。前者${facetStateText(af)}，後者${facetStateText(bf)}。`;
}

function interactionPattern(a:FullChartThread,b:FullChartThread,facets:readonly [TopicSynthesisFacet,TopicSynthesisFacet]):string {
  const [af,bf]=facets;
  const aScenario=af.scenarioExamples[0] ?? `觀察「${af.label}」何時最明顯`;
  const bScenario=bf.scenarioExamples[0] ?? `觀察「${bf.label}」何時最明顯`;
  return `可以對照兩個實際情境：第一，${aScenario}；第二，${bScenario}。`;
}

function interactionBalance(id:string,a:FullChartThread,b:FullChartThread,facets:readonly [TopicSynthesisFacet,TopicSynthesisFacet]):string {
  const [af,bf]=facets;
  const relational=INTERACTION_ROLE_READING[id] ?? `把「${af.label}」與「${bf.label}」放在同一個生活決策裡看，重點是辨認兩者何時同時出現，以及一邊的做法是否會壓縮另一邊的調整空間。`;
  const state=a.counterCount>0||b.counterCount>0
    ? '這組目前至少一邊帶有需要調整的條件，所以先看壓力出現時哪個環節最先失去彈性。'
    : '這組目前兩邊都較有可運用條件，所以重點放在如何讓兩種做法彼此支援，而不是重複放大同一種優勢。';
  return `${relational}${state}`;
}
function interactionFitReason(a:FullChartThread,b:FullChartThread,facets:readonly [TopicSynthesisFacet,TopicSynthesisFacet]):string {
  const shared=sharedFacetBasis(facets[0],facets[1]);
  const reasons:string[]=[];
  if(a.counterCount>0||b.counterCount>0) reasons.push('至少一邊帶有實際反向條件');
  if(shared.length) reasons.push(`兩個主題都出現「${shared.map((label)=>READER_BASIS_LABEL[label]??'相同修正條件').join('、')}」`);
  if(a.facets.length>1||b.facets.length>1) reasons.push('至少一個主題有多個獨立白話維度');
  if(!reasons.length) reasons.push('兩個主題都有正式規則形成可閱讀內容');
  return `本盤排序依據：實際出現${reasons.join('，而且')}。`;
}

const REPEATABLE_BASIS=PRIORITY_BASIS;

function structuralConvergences(threads:readonly FullChartThread[], syntheses:readonly TopicSynthesisViewModel[]):readonly FullChartStructuralConvergence[]{
  const threadTopics=new Set(threads.map((item)=>item.topic));
  return Object.freeze(REPEATABLE_BASIS.flatMap((label)=>{
    const topics=syntheses.filter((item)=>threadTopics.has(item.topic)&&item.facets.some((facet)=>facet.basisLabels.includes(label))).map((item)=>item.topic);
    if(topics.length<2) return [];
    const names=topics.map((topic)=>TOPIC_LABEL[topic]??topic);
    return [Object.freeze({id:`basis-${label}`,label,topics:Object.freeze(topics),reading:`「${label}」在 ${names.join('、')} 都有實際觸發的判讀依據。這表示同一類盤面結構重複參與不同生活主題；它可以提高合參優先度，但不能把不同宮位直接合併成同一件事或同一個結果。`})];
  }));
}
function tensionItems(topics:readonly TopicInterpretationViewModel[]):readonly FullChartTension[]{
  return Object.freeze(topics.flatMap((topic)=>{
    const c=topicCounts(topic);
    if(c.counter===0)return [];
    const label=TOPIC_LABEL[topic.topic]??topic.topic;
    const reading=c.support>0
      ? `「${label}」同時有 ${c.support} 組支持與 ${c.counter} 組反向條件，屬於盤內張力：有可運用的傾向，也有會讓同一主題需要調整策略的牽制。`
      : `「${label}」目前觸發 ${c.counter} 組反向條件，尚沒有已核准的支持規則同時出現；應把它讀成此主題需要多一層調整，不是負面結果判定。`;
    return [Object.freeze({id:`tension-${topic.topic}`,topic:topic.topic,topicLabel:label,reading})];
  }));
}

export function buildFullChartSynthesis(
  topics: readonly TopicInterpretationViewModel[],
  syntheses: readonly TopicSynthesisViewModel[],
): FullChartSynthesisViewModel {
  const synthesisMap=new Map(syntheses.map((item)=>[item.topic,item] as const));
  const topicMap=new Map(topics.filter((item)=>item.topic!=='overview'&&item.topic!=='timing').map((item)=>[item.topic,item] as const));
  const threads=Object.freeze([...topicMap.values()].map((topic)=>threadFor(topic,synthesisMap.get(topic.topic))).filter((item):item is FullChartThread=>Boolean(item)));
  const threadMap=new Map(threads.map((item)=>[item.topic,item] as const));

  const interactions=Object.freeze(ALLOWED_INTERACTION_DEFS.flatMap((def)=>{
    const a=threadMap.get(def.a);
    const b=threadMap.get(def.b);
    if(!a||!b) return [];
    const priority=pairPriority(a,b);
    const facets=bestFacetPair(a,b);
    return [Object.freeze({
      id:def.id, label:def.label, topics:Object.freeze([def.a,def.b]), priority,
      facetLabels:Object.freeze([facets[0].label,facets[1].label]),
      reading:interactionReading(a,b,def.bridge,facets),
      patternReading:interactionPattern(a,b,facets),
      balanceReading:interactionBalance(def.id,a,b,facets),
      fitReason:interactionFitReason(a,b,facets),
    })];
  }).sort((left,right)=>right.priority-left.priority||left.id.localeCompare(right.id)).slice(0,6));
  const convergenceItems=structuralConvergences(threads,syntheses);
  const tensions=tensionItems([...topicMap.values()]);
  const totals=[...topicMap.values()].reduce((acc,topic)=>{
    const c=topicCounts(topic);
    return {support:acc.support+c.support,counter:acc.counter+c.counter,claims:acc.claims+topic.claims.length};
  },{support:0,counter:0,claims:0});
  const balanceSummary=totals.counter>0
    ? `全盤目前共有 ${totals.claims} 組已觸發結論，合計 ${totals.support} 組支持與 ${totals.counter} 組反向條件。正反訊號並存時，應優先看它出現在哪個生活主題與什麼情境，而不是把它們加總成單一吉凶。`
    : `全盤目前共有 ${totals.claims} 組已觸發結論，合計 ${totals.support} 組支持條件；沒有觸發反向規則不代表人生沒有阻力，只代表目前已核准規則在本盤的觸發狀況。`;
  const missing=[...topicMap.values()].filter((topic)=>topic.claims.length===0).map((topic)=>TOPIC_LABEL[topic.topic]??topic.topic);
  const coverageSummary=missing.length
    ? `已有正式規則形成判讀的主題為 ${threads.map((item)=>item.topicLabel).join('、')||'目前沒有'}；${missing.join('、')}目前仍缺足夠規則，因此不以無來源文字補成結論。`
    : '目前八個主要本命主題都有至少一組正式規則觸發；各主題的規則數量與語義深度仍不同，不應被誤解成相同。';

  return Object.freeze({
    model:'FULL-CHART-SYNTHESIS-0.7.0',
    headline:'全盤合參主軸',
    lead:'這裡不是把固定的兩個分類硬湊在一起，而是先看這張盤實際觸發哪些生活模式，再把最值得一起驗證的幾組關係排到前面。',
    threads, interactions, structuralConvergences:convergenceItems, tensions, balanceSummary, coverageSummary,
    disclosure:'全盤合參只重組已觸發的正式規則與白話維度；排序只決定閱讀優先度，不新增星曜吉凶、因果關係、事件預測或未經來源審核的命理規則。',
  });
}
